const COOKIE_NAME = "prophero_language";
const COOKIE_DOMAIN = ".prophero.com";
const LS_KEY = "prophero::language";

type Language = "en" | "es";

/** Used when the user has no stored choice and Auth0 sends no supported locale. */
export const DEFAULT_LANGUAGE: Language = "en";

function isValidLanguage(value: string | null): value is Language {
  return value === "en" || value === "es";
}

function readStoredLanguage(): Language | null {
  try {
    if (typeof window === "undefined") return null;

    const match = document.cookie.match(/(?:^|; )prophero_language=([^;]*)/);
    const cookie = match ? decodeURIComponent(match[1]) : null;
    if (isValidLanguage(cookie)) return cookie;

    const ls = localStorage.getItem(LS_KEY);
    if (isValidLanguage(ls)) return ls;

    return null;
  } catch {
    return null;
  }
}

export function getStoredLanguage(): Language {
  return readStoredLanguage() ?? DEFAULT_LANGUAGE;
}

/** True when the user has explicitly picked a language (cookie or localStorage). */
export function hasStoredLanguage(): boolean {
  return readStoredLanguage() !== null;
}

export function storeLanguage(lang: string): void {
  document.cookie = `${COOKIE_NAME}=${lang}; Domain=${COOKIE_DOMAIN}; Path=/; SameSite=Lax; Secure; Max-Age=31536000`;
  try {
    localStorage.setItem(LS_KEY, lang);
  } catch {
    /* blocked */
  }
}

const UI_LOCALES_APPLIED_KEY = "prophero::ui-locales-applied";

/**
 * Picks the first supported language from an OIDC `ui_locales` value
 * (space-separated BCP 47 tags, e.g. "es-ES en").
 */
export function resolveUiLocales(
  uiLocales: string | null | undefined
): Language | null {
  if (!uiLocales) return null;
  for (const tag of uiLocales.trim().split(/\s+/)) {
    const base = tag.split("-")[0].toLowerCase();
    if (isValidLanguage(base)) return base;
  }
  return null;
}

/**
 * Whether this `ui_locales` request (per transaction + language) was already applied.
 * Auth0 repeats `ui_locales` on every screen of a transaction, so re-applying it
 * would undo a language the user picked in-page afterwards.
 */
export function hasAppliedUiLocales(requestKey: string): boolean {
  try {
    return sessionStorage.getItem(UI_LOCALES_APPLIED_KEY) === requestKey;
  } catch {
    return false;
  }
}

export function markUiLocalesApplied(requestKey: string): void {
  try {
    sessionStorage.setItem(UI_LOCALES_APPLIED_KEY, requestKey);
  } catch {
    /* blocked */
  }
}
